import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import DocumentCard from './components/DocumentCard';

const LegalDocumentsList = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ documentType: '', status: '' });
  const [stats, setStats] = useState({ total: 0, draft: 0, published: 0, archived: 0 });

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.documentType) params.append('documentType', filter.documentType);
      if (filter.status) params.append('status', filter.status);
      
      const response = await api.get(`/api/super-admin/legal-documents?${params.toString()}`);
      setDocuments(response.data.data);
      
      // Calculate stats
      const allDocs = response.data.data;
      setStats({
        total: allDocs.length,
        draft: allDocs.filter(d => d.status === 'draft').length,
        published: allDocs.filter(d => d.status === 'published').length,
        archived: allDocs.filter(d => d.status === 'archived').length,
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
      alert('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handlePublish = async (id) => {
    if (!window.confirm('Are you sure you want to publish this document? This will archive the previous version.')) return;
    
    try {
      await api.post(`/api/super-admin/legal-documents/${id}/publish`);
      fetchDocuments();
      alert('Document published successfully!');
    } catch (error) {
      console.error('Error publishing document:', error);
      alert('Failed to publish document');
    }
  };

  const handleArchive = async (id) => {
    if (!window.confirm('Are you sure you want to archive this document?')) return;
    
    try {
      await api.post(`/api/super-admin/legal-documents/${id}/archive`);
      fetchDocuments();
      alert('Document archived successfully!');
    } catch (error) {
      console.error('Error archiving document:', error);
      alert('Failed to archive document');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this draft? This action cannot be undone.')) return;
    
    try {
      await api.delete(`/api/super-admin/legal-documents/${id}`);
      fetchDocuments();
      alert('Document deleted successfully!');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const documentTypes = [
    { value: '', label: 'All Document Types' },
    { value: 'terms_of_service', label: 'Terms of Service' },
    { value: 'privacy_policy', label: 'Privacy Policy' },
    { value: 'refund_policy', label: 'Refund Policy' },
    { value: 'cookie_policy', label: 'Cookie Policy' },
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 antialiased p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-200">
          <div>
            <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wider block mb-1">
              Super Admin Control Center
            </span>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
              Legal Documents
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 font-medium">
              Manage platform terms of service, privacy policies, refund terms, and version lifecycles.
            </p>
          </div>

          <button
            onClick={() => navigate('/super-admin/legal-documents/create')}
            className="py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs sm:text-sm rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 shadow-xs flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Create New Document
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-xs">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Documents</p>
            <p className="text-3xl font-extrabold text-gray-900 mt-2">{stats.total}</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-amber-200 shadow-xs">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Drafts</p>
            <p className="text-3xl font-extrabold text-amber-600 mt-2">{stats.draft}</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-emerald-200 shadow-xs">
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Published</p>
            <p className="text-3xl font-extrabold text-emerald-600 mt-2">{stats.published}</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-xs">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Archived</p>
            <p className="text-3xl font-extrabold text-slate-500 mt-2">{stats.archived}</p>
          </div>
        </div>

        {/* Filters and Navigation Bar */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-xs">
          <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4">
            
            {/* Filter Dropdowns */}
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
              <select
                value={filter.documentType}
                onChange={(e) => setFilter({ ...filter, documentType: e.target.value })}
                className="w-full sm:w-auto px-3.5 py-2 bg-gray-50 border border-gray-300 rounded-xl text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all font-medium"
              >
                {documentTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="w-full sm:w-auto px-3.5 py-2 bg-gray-50 border border-gray-300 rounded-xl text-xs sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all font-medium"
              >
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
            </div>

            {/* Tenant Acceptance Navigation Button */}
            <button
              onClick={() => navigate('/super-admin/legal-documents/tenant-acceptance')}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs sm:text-sm rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-slate-700 flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View Tenant Acceptance
            </button>

          </div>
        </div>

        {/* Documents List View */}
        {loading ? (
          <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-3 border-indigo-600 border-t-transparent"></div>
            <p className="mt-4 text-sm font-medium text-gray-500">Loading legal documents...</p>
          </div>
        ) : documents.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-12 text-center">
            <div className="w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-700">No matching legal documents found</p>
            <p className="text-xs text-gray-400 mt-1 mb-6">Create a document draft or adjust your search filters.</p>
            <button
              onClick={() => navigate('/super-admin/legal-documents/create')}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium text-xs rounded-xl transition-colors"
            >
              Create your first document
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {documents.map(doc => (
              <DocumentCard
                key={doc._id}
                document={doc}
                onPublish={() => handlePublish(doc._id)}
                onArchive={() => handleArchive(doc._id)}
                onDelete={() => handleDelete(doc._id)}
                onEdit={() => navigate(`/super-admin/legal-documents/edit/${doc._id}`)}
                onView={() => navigate(`/super-admin/legal-documents/details/${doc._id}`)}
                onViewHistory={() => navigate(`/super-admin/legal-documents/versions/${doc.documentType}`)}
              />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default LegalDocumentsList;